<script setup>
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import { navRoutes } from '@/router.js';
import { useAuthStore } from '@/stores/auth.js';
import AppIcon from '@/components/AppIcon.vue';

const route  = useRoute();
const router = useRouter();
const auth   = useAuthStore();

async function logout() {
  await auth.logout();
  router.replace({ name: 'login' });
}
</script>

<template>
  <div class="shell">

    <!-- ── Left navigation ─────────────────────────────────────────────── -->
    <nav class="sidenav">
      <div class="brand">
        <span class="brand__mark">❦</span>
        <span class="brand__name">Garden</span>
      </div>

      <RouterLink
        v-for="r in navRoutes" :key="r.name"
        :to="{ name: r.name }" class="navlink"
        :class="{ 'navlink--active': route.name === r.name }"
      >
        <AppIcon :name="r.meta.icon" />
        <span class="navlink__label">{{ r.meta.title }}</span>
      </RouterLink>

      <div class="sidenav__spacer" />

      <!-- User pill + logout -->
      <div v-if="auth.user" class="user">
        <span class="user__email">{{ auth.user.email }}</span>
        <button class="user__logout" title="Log ud" @click="logout">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </nav>

    <!-- ── Content ─────────────────────────────────────────────────────── -->
    <main class="content">
      <RouterView v-slot="{ Component }">
        <component :is="Component" />
      </RouterView>
    </main>

    <!-- ── Bottom navigation (mobile only) ───────────────────────────── -->
    <nav class="bottomnav">
      <RouterLink
        v-for="r in navRoutes" :key="r.name"
        :to="{ name: r.name }" class="botlink"
        :class="{ 'botlink--active': route.name === r.name }"
      >
        <AppIcon :name="r.meta.icon" />
        <span>{{ r.meta.title }}</span>
      </RouterLink>
      <!-- Logout on mobile -->
      <button v-if="auth.user" class="botlink" @click="logout" title="Log ud">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
             stroke-linecap="round" stroke-linejoin="round" style="width:22px;height:22px">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
          <polyline points="16 17 21 12 16 7"/>
          <line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        <span>Log ud</span>
      </button>
    </nav>

  </div>
</template>

<style scoped>
.shell { min-height: 100%; display: flex; }

/* ── Sidebar ────────────────────────────────────────────────────────────── */
.sidenav {
  position: sticky; top: 0; align-self: flex-start;
  height: 100vh; width: var(--nav-side); flex: 0 0 var(--nav-side);
  display: flex; flex-direction: column; gap: 4px;
  padding: 20px 14px;
  background: var(--surface); border-right: 1px solid var(--border);
}
.brand { display: flex; align-items: center; gap: 11px; padding: 6px 10px 22px; }
.brand__mark { color: var(--leaf); font-size: 1.5rem; line-height: 1; }
.brand__name { font-family: var(--font-display); font-weight: 700; font-size: 1.3rem; letter-spacing: -0.02em; }

.navlink {
  display: flex; align-items: center; gap: 13px;
  padding: 12px; border-radius: var(--radius-sm);
  color: var(--muted); font-weight: 500;
  border: 1px solid transparent;
  transition: color .15s, background .15s, border-color .15s;
}
.navlink:hover { color: var(--text); background: var(--surface-2); }
.navlink--active { color: var(--leaf); background: var(--leaf-glow); border-color: var(--border-2); }
.navlink__label { white-space: nowrap; }

.sidenav__spacer { flex: 1; }

.user {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--border);
  margin-top: 4px;
}
.user__email {
  flex: 1; min-width: 0;
  font-size: .78rem; color: var(--muted);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.user__logout {
  flex-shrink: 0;
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  color: var(--muted); background: none; border: none; cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color .15s, background .15s;
}
.user__logout:hover { color: var(--danger); background: rgba(239,111,111,.1); }
.user__logout svg { width: 17px; height: 17px; }

/* ── Content ────────────────────────────────────────────────────────────── */
.content { flex: 1 1 auto; min-width: 0; min-height: 100vh; }

/* ── Bottom nav (mobile) ────────────────────────────────────────────────── */
.bottomnav { display: none; }

/* ── Tablet: icon rail ──────────────────────────────────────────────────── */
@media (max-width: 1023px) {
  .sidenav { width: var(--nav-rail); flex-basis: var(--nav-rail); padding: 18px 10px; align-items: center; }
  .brand__name, .navlink__label, .user__email { display: none; }
  .navlink { justify-content: center; }
  .user { justify-content: center; padding: 10px 0; }
}

/* ── Mobile: bottom bar ─────────────────────────────────────────────────── */
@media (max-width: 719px) {
  .sidenav { display: none; }
  .content { min-height: calc(100vh - var(--bottom-nav)); padding-bottom: var(--bottom-nav); }
  .bottomnav {
    display: flex; position: fixed; bottom: 0; left: 0; right: 0; z-index: 50;
    height: calc(var(--bottom-nav) + env(safe-area-inset-bottom, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: var(--surface); border-top: 1px solid var(--border);
  }
  .botlink {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 3px; color: var(--muted); font-size: .66rem; font-weight: 600;
    background: none; border: none; cursor: pointer;
  }
  .botlink--active { color: var(--leaf); }
}
</style>
